import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConnectorsModal } from './ConnectorsModalProvider';
import ConnectorCard from './ConnectorCard';
import { ConnectorGroup, groupFor, searchWordsFor } from './connectorGroups';
import type { ConnectorRegistryEntry } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useConnectorConnect } from './useConnectorConnect';
import { realAccountLabel } from './copy';

/**
 * ConnectorsOverviewModal — the "browse + search" surface lifted out of
 * the old standalone /connectors page. Provider mounts this when the URL
 * is `?connectors` (and not yet narrowed to a specific connector).
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  Connectors                          [🔍 search]    [×]     │
 *   │  Add tools your AI coworker can use.                         │
 *   │                                                              │
 *   │  ┌─ HeyGen ─┐  ┌─ + More ─┐                                 │
 *   │  │ ...      │  │  coming  │                                 │
 *   │  └──────────┘  └──────────┘                                 │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Visual tokens match the existing Connectors page (lifted verbatim from
 * Connectors.tsx 200-285) so the modal feels like the same surface, just
 * re-housed. Mobile: full-screen instead of bottom-sheet (the grid needs
 * the room).
 */
const ConnectorsOverviewModal: React.FC = () => {
  const {
    registry,
    connectionByConnectorId,
    loading,
    close,
    openForConnector,
    refresh,
  } = useConnectorsModal();
  const { error: notifyError, success: notifySuccess } = useNotification();

  // Local-only state: search query + per-card connecting spinner.
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Held for transition: Connect button on the card transitions to the
  // Manage panel where the universal-key Enable CTA lives. There's no
  // longer an inline busy state for "Connect" — kept as a stable signature
  // for the ConnectorCard prop but always null in practice.
  const [connectingId] = useState<string | null>(null);
  // Bring-your-own connectors (connectMode 'connect') connect from the card
  // itself: one click to the service's consent page, or, for GitHub, done on
  // the spot.
  const byo = useConnectorConnect({
    onConnected: async ({ connectorId, accountLabel }) => {
      const entry = registry.find((e) => e.id === connectorId);
      const label = realAccountLabel(accountLabel, entry?.displayName);
      notifySuccess(label ? `Connected as ${label}.` : `${entry?.displayName || 'App'} is ready`);
      await refresh();
    },
  });

  // Debounce mirrors the original Connectors page (150ms). Short enough to
  // feel instant, long enough that filter+rerender doesn't run on every
  // keystroke once the registry grows.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 150);
    return () => clearTimeout(t);
  }, [query]);

  // ESC / backdrop close. Focus moves to search on mount so keyboard users
  // can immediately type a filter.
  const searchRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) close();
    },
    [close]
  );

  // Click Connect on a card → open the Manage panel where the
  // universal-key Enable CTA lives. The legacy OAuth popup that used to
  // fire here was removed in Wave 3 of the HeyGen migration.
  const handleConnect = useCallback(
    (id: string) => {
      const entry = registry.find((e) => e.id === id);
      if (entry && entry.connectMode === 'connect') {
        void byo.connect(entry);
        return;
      }
      openForConnector(id);
    },
    [openForConnector, registry, byo]
  );
  // notifyError is used by future error paths; suppress unused-var warning.
  void notifyError;

  const handleManage = useCallback(
    (id: string) => {
      // Pass no triggerRef here — the trigger that opened the overview
      // (the "+" button or sidebar link) is what we want focus to return
      // to when the entire modal closes, not the card itself. The card
      // gets focus on breadcrumb-back via a separate code path.
      openForConnector(id);
    },
    [openForConnector]
  );

  const filteredRegistry = useMemo(() => {
    if (!debouncedQuery) return registry;
    return registry.filter((e) => {
      const haystack = `${e.displayName} ${e.tagline} ${searchWordsFor(e.category)}`.toLowerCase();
      return haystack.includes(debouncedQuery);
    });
  }, [registry, debouncedQuery]);

  // No search: one labelled section per group, in the registry's (already
  // group-sorted) order. A search shows one flat list of matches.
  const groups = useMemo(() => {
    const out: { group: ConnectorGroup; entries: ConnectorRegistryEntry[] }[] = [];
    for (const entry of registry) {
      const group = groupFor(entry.category);
      const last = out[out.length - 1];
      if (last && last.group.id === group.id) last.entries.push(entry);
      else out.push({ group, entries: [entry] });
    }
    return out;
  }, [registry]);

  const renderCard = (entry: ConnectorRegistryEntry) => (
    <ConnectorCard
      key={entry.id}
      entry={entry}
      connection={connectionByConnectorId[entry.id] || null}
      onConnect={handleConnect}
      onManage={handleManage}
      isBusy={
        entry.connectMode === 'connect'
          ? byo.isBlocked(entry.id)
          : connectingId === entry.id
      }
      message={entry.connectMode === 'connect' ? byo.messageFor(entry.id) : null}
    />
  );

  // A plain footnote: a dashed box with a "+" read as an Add button that did
  // nothing when clicked.
  const placeholder = (
    <p data-testid="connector-placeholder" className="mt-8 text-center text-xs text-white/35">
      More connectors coming.
    </p>
  );

  // Same rule as ConnectorCard's green "Connected" pill, so the header count
  // never disagrees with the cards below it.
  const connectedCount = useMemo(
    () =>
      registry.filter((e) => {
        const s = connectionByConnectorId[e.id]?.status;
        if (e.connectMode === 'connect') return s === 'connected' || s === 'error';
        return s === 'connected' || s === 'enabled';
      }).length,
    [registry, connectionByConnectorId]
  );

  const gridClass = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="connectors-overview-title"
      className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center bg-black/70 backdrop-blur-md sm:p-6 animate-backdrop-in"
      onClick={handleBackdrop}
      data-testid="connectors-overview-modal"
    >
      <div
        ref={dialogRef}
        className="
          flex-1 sm:flex-initial
          w-full sm:max-w-4xl
          sm:rounded-2xl
          bg-[#141416] sm:border sm:border-white/[0.07]
          sm:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6)]
          sm:max-h-[min(860px,calc(100vh-3rem))]
          overflow-hidden flex flex-col
          animate-dialog-in
        "
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4 sm:px-7 sm:pt-6 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2
                id="connectors-overview-title"
                className="text-lg sm:text-xl font-semibold tracking-tight text-white"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Connectors
              </h2>
              <p className="mt-1 text-sm text-white/50">
                Add tools your AI coworker can use.
                {connectedCount > 0 && (
                  <span className="text-white/35" data-testid="connectors-overview-connected-count">
                    {' '}· {connectedCount} connected
                  </span>
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="-mr-1.5 -mt-1 inline-flex items-center justify-center w-9 h-9 max-md:w-11 max-md:h-11 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 flex-shrink-0"
              data-testid="connectors-overview-close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="relative mt-4">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-white/35"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search, e.g. email or payments"
              aria-label="Search connectors"
              data-testid="connectors-overview-search"
              className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border border-white/[0.07] text-sm text-white placeholder-white/35 focus:outline-none focus:bg-white/[0.06] focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 transition"
            />
          </div>
        </div>

        {/* Body — scrolls inside the modal */}
        <div className="flex-1 overflow-y-auto px-5 pb-6 pt-2 sm:px-7 sm:pb-7 border-t border-white/[0.05]">
          {loading ? (
            <div className={`${gridClass} mt-5`} aria-label="Loading connectors">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  data-testid="connector-skeleton"
                  className="rounded-xl p-4 bg-white/[0.025] border border-white/[0.06] h-[148px] animate-pulse"
                >
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-[10px] bg-white/[0.08] shrink-0" />
                    <div className="flex-1 pt-0.5">
                      <div className="h-3.5 w-24 bg-white/[0.08] rounded mb-2.5" />
                      <div className="h-3 w-full bg-white/[0.06] rounded mb-1.5" />
                      <div className="h-3 w-2/3 bg-white/[0.06] rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div aria-label="Available connectors">
              {debouncedQuery ? (
                <div className={`${gridClass} mt-5`}>
                  {filteredRegistry.map(renderCard)}
                  {filteredRegistry.length === 0 && (
                    <div
                      data-testid="connectors-overview-empty"
                      className="col-span-full flex flex-col items-center py-14 text-center"
                    >
                      <span className="mb-3 inline-flex w-10 h-10 items-center justify-center rounded-full bg-white/[0.04] text-white/40" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="7" />
                          <path d="m21 21-4.3-4.3" />
                        </svg>
                      </span>
                      <p className="text-sm text-white/70">No connectors match &ldquo;{query}&rdquo;.</p>
                      <p className="mt-1 text-xs text-white/40">Try a word for what you need, like &ldquo;email&rdquo; or &ldquo;payments&rdquo;.</p>
                    </div>
                  )}
                </div>
              ) : (
                groups.map(({ group, entries }) => (
                  <section
                    key={group.id}
                    aria-labelledby={`connector-group-${group.id}`}
                    className="mt-6"
                  >
                    <h3
                      id={`connector-group-${group.id}`}
                      className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40"
                    >
                      {group.label}
                    </h3>
                    <div className={gridClass}>{entries.map(renderCard)}</div>
                  </section>
                ))
              )}
              {(!debouncedQuery || filteredRegistry.length > 0) && placeholder}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectorsOverviewModal;
